/**
 * Analytics service for tracking user behavior.
 * Uses PostHog with anonymous/cookieless tracking (no consent popup required).
 */

type EventProperties = Record<string, string | number | boolean | null | undefined>;

class AnalyticsService {
  private posthog: { capture: (event: string, properties?: EventProperties) => void } | null = null;

  initialize(posthogInstance: { capture: (event: string, properties?: EventProperties) => void }): void {
    this.posthog = posthogInstance;
  }

  track(eventName: string, properties?: EventProperties): void {
    if (!this.posthog) {
      if (__DEV__) {
        console.log(`[Analytics] ${eventName}`, properties);
      }
      return;
    }
    this.posthog.capture(eventName, properties);
  }

  /**
   * Sanitize search queries to avoid sending obvious PII (emails/phone numbers)
   * to the analytics provider.
   */
  private sanitizeQuery(query: string): string {
    const emailPattern = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
    const phonePattern = /\+?\d[\d\s().-]{7,}\d/g;

    return query
      .replace(emailPattern, '[redacted]')
      .replace(phonePattern, '[redacted]');
  }

  trackScreenView(screenName: string): void {
    this.track('screen_view', { screen_name: screenName });
  }

  trackSearch(query: string, resultsCount: number): void {
    const normalizedQuery = query.toLowerCase().trim();
    const safeQuery = this.sanitizeQuery(normalizedQuery);
    this.track('search', { query: safeQuery, results_count: resultsCount });
  }

  trackSignup(): void {
    this.track('signup_completed');
  }

  trackListingCreated(category?: string): void {
    this.track('listing_created', { category });
  }

  trackItemView(itemId: string): void {
    this.track('item_view', { item_id: itemId });
  }

  trackTransactionCompleted(): void {
    this.track('transaction_completed');
  }

  // ─── Funnel events (traction metrics) ───────────────────────────────────

  trackItemViewed(category?: string): void {
    this.track('item_viewed', { category });
  }

  trackChatStarted(): void {
    this.track('chat_started');
  }

  trackOfferCreated(): void {
    this.track('offer_created');
  }

  trackOfferAccepted(): void {
    this.track('offer_accepted');
  }

  trackDealAgreed(): void {
    this.track('deal_agreed');
  }

  trackHandoverConfirmed(): void {
    this.track('handover_confirmed');
  }

  trackPaymentLinkShared(): void {
    this.track('payment_link_shared');
  }
}

export const analyticsService = new AnalyticsService();
